import { useMemo, useEffect } from "react";
import { Stack, IconButton } from "@fluentui/react";
import DOMPurify from "dompurify";
import { AskResponse, getCitationFilePath } from "../../api";
import styles from "./Answer.module.css";
import { parseAnswerToHtml } from "./AnswerParser";
import { AnswerIcon } from "./AnswerIcon";
import { AnswerLinks, originLink } from "./AnswerLinks";
import { AnswerLinksProcess } from "./AnswerLinksProcess";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { pullHistQuestions } from "../../components/History/HistoryComponent";
import { useUser } from "../../contexts/useUserData";
import { useFeatureFlags } from "../../contexts/useFeatureFlags";

interface Props {
    answer: AskResponse;
    isSelected?: boolean;
    onCitationClicked: (filePath: string) => void;
    onThoughtProcessClicked: () => void;
    onSupportingContentClicked: () => void;
    onFollowupQuestionClicked?: (question: string) => void;
    showFollowupQuestions?: boolean;
    linkToken?: string;
    seccion?: string;
}

export const Answer = ({
    answer,
    isSelected,
    onCitationClicked,
    onThoughtProcessClicked,
    onSupportingContentClicked,
    onFollowupQuestionClicked,
    showFollowupQuestions,
    linkToken,
    seccion
}: Props) => {
    const { user, scope, pushReceivedHistQuestions, removeHistQuestions, loginMethod } = useUser(); // Obtén valores del contexto
    const { promocionesIndexEnabled } = useFeatureFlags();

    const arraydocs = useMemo(() => AnswerLinksProcess({ answer, onCitationClicked }), [answer]);
    const parsedAnswer = useMemo(() => parseAnswerToHtml(arraydocs.linksFiltered, answer, answer.answer, onCitationClicked), [answer]);

    const debug_data = answer.debug_data;

    let sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml);
    console.log("=====sanitizedAnswerHtml======");
    console.log(sanitizedAnswerHtml);
    console.log("=====End sanitizedAnswerHtml======");

    let origin = originLink(linkToken!, seccion!);

    // Índice de promociones (visibilidad controlada por flag de runtime del backend)
    if (promocionesIndexEnabled && answer && answer.docs_guia && answer.docs_guia.length > 0) {
        const url = "/render/genericRender.xhtml?id=";
        let paragraph;
        const docid1 = answer.docs_guia[0].id;
        const title1 = answer.docs_guia[0].title;
        const href1 = origin + url + `${docid1}` + "&type=0" + '&source=chat';
        if (answer.docs_guia.length > 1) {
            const docid2 = answer.docs_guia[1].id;
            const href2 = origin + url + `${docid2}` + "&type=0" + '&source=chat';
            const title2 = answer.docs_guia[1].title;
            paragraph = `<p>También puedes consultar más promociones en el documento <a href="${href1}" target="_blank">${title1}</a> y <a href="${href2}" target="_blank">${title2}.</a></p>`;
        } else {
            paragraph = `<p>También puedes consultar más información en el documento <a href="${href1}" target="_blank">${title1}.</a></p>`;
        }
        sanitizedAnswerHtml += "\n" + paragraph;
    }

    // Índice de herramientas (solo B2C)
    if (seccion === "B2C" && answer && answer.docs_herramienta && answer.docs_herramienta.length > 0) {
        const tool = answer.docs_herramienta[0];
        const toolUrl = "/render/genericRender.xhtml?id=";
        const toolHref = origin + toolUrl + `${tool.id}` + "&type=0" + '&source=chat';
        const toolParagraph = `<p>Para calcular el ahorro te puede interesar consultar <a href="${toolHref}" target="_blank">${tool.title}</a></p>`;
        sanitizedAnswerHtml += "\n" + toolParagraph;
    }

    function processText(inputText: string): string {
        let result = inputText;
        let output = "";
        let inRed = false;
        let cursor = 0;
    
        const regex = /\(\(False\)\)|\(\(True\)\)/g;
        let match;
    
        while ((match = regex.exec(result)) !== null) {
            let matchText = match[0];
            let matchIndex = match.index;
    
            // Captura el texto previo, verificando si es parte de una lista numerada o viñeta
            let segment = result.substring(cursor, matchIndex);
            
            if (inRed) {
                // Asegura que los bullets o numeración no sean afectados
                segment = segment.replace(/(^|\n)(\d+\.\s+|\-\s+)/g, "$1$2<span style=\"color:#7b0404;\">") + "</span>";
                segment = `<span style="color:#7b0404;">${segment}</span>`;
            }
    
            output += segment;
    
            // Alternamos el estado de inRed según el marcador encontrado
            if (matchText === "((False))") {
                inRed = true;
            } else if (matchText === "((True))") {
                inRed = false;
            }
    
            cursor = matchIndex + matchText.length;
        }
    
        // Agregamos el texto restante, asegurando el cierre de etiquetas si es necesario
        let remainingText = result.substring(cursor);
        if (inRed) {
            remainingText = remainingText.replace(/(^|\n)(\d+\.\s+|\-\s+)/g, "$1$2<span style=\"color:#7b0404;\">") + "</span>";
            remainingText = `<span style="color:#7b0404;">${remainingText}</span>`;
        }
        output += remainingText;
    
        // Eliminamos los marcadores ((False)) y ((True))
        output = output.replace(/\(\(False\)\)|\(\(True\)\)/g, "");
    
        return output;
    }

    const processedHtml = processText(sanitizedAnswerHtml);

    // Llama a pullHistQuestions al cargar la respuesta
    useEffect(() => {
        if (answer && user && scope && pushReceivedHistQuestions && removeHistQuestions) {
            const startTimestamp = new Date().getTime(); // Timestamp antes de la llamada en milisegundos
            console.log("Invocando pullHistQuestions desde Answer...");
            console.log("Inicio de pullHistQuestions:", new Date(startTimestamp).toISOString());
    
            pullHistQuestions(scope, pushReceivedHistQuestions, removeHistQuestions, seccion, loginMethod);
    
            const endTimestamp = new Date().getTime(); // Timestamp después de la llamada en milisegundos
            console.log("Fin de pullHistQuestions:", new Date(endTimestamp).toISOString());
            console.log("Duración de pullHistQuestions (ms):", endTimestamp - startTimestamp);
        } else {
            console.warn("Faltan datos para ejecutar pullHistQuestions", {
                user,
                scope,
                pushReceivedHistQuestions,
                removeHistQuestions
            });
        }
    }, [answer, user, scope]);

    return (
        <Stack className={`${styles.answerContainer} ${isSelected && styles.selected}`} verticalAlign="space-between">
            <Stack.Item>
                <Stack horizontal horizontalAlign="space-between">
                    <AnswerIcon />
                    <div>
                        <IconButton
                            style={{ color: "black" }}
                            iconProps={{ iconName: "Lightbulb" }}
                            title="Show thought process"
                            ariaLabel="Show thought process"
                            onClick={() => onThoughtProcessClicked()}
                            disabled={!answer.thoughts}
                        />
                        <IconButton
                            style={{ color: "black" }}
                            iconProps={{ iconName: "ClipboardList" }}
                            title="Show supporting content"
                            ariaLabel="Show supporting content"
                            onClick={() => onSupportingContentClicked()}
                            disabled={!answer.data_points.length}
                        />
                    </div>
                </Stack>
            </Stack.Item>
    
            <Stack.Item grow>
                <div
                    className={styles.answerText}
                    onClick={(e) => {
                        const targetElement = e.target as HTMLElement;
                        for (let id = 0; id < arraydocs.linksFiltered.length; id++) {
                            if (targetElement.tagName === "SUP" && targetElement.innerHTML == (id + 1).toString())
                                onCitationClicked(getCitationFilePath(targetElement.title));
                        }
                    }}
                >
                    <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                        {processedHtml}
                    </Markdown>
                </div>
            </Stack.Item>
            {!!answer.data_points?.length && (
                <AnswerLinks arraydocs={arraydocs} citacion={parsedAnswer.citations} answer={answer} onCitationClicked={onCitationClicked} origin={linkToken!} seccion={seccion!} />
            )}
    
            {!!parsedAnswer.followupQuestions.length && showFollowupQuestions && onFollowupQuestionClicked && (
                <Stack.Item>
                    <Stack horizontal wrap className={`${!!parsedAnswer.citations.length ? styles.followupQuestionsList : ""}`} tokens={{ childrenGap: 6 }}>
                        <span className={styles.followupQuestionLearnMore}>Follow-up questions:</span>
                        {parsedAnswer.followupQuestions.map((x, i) => {
                            return (
                                <a key={i} className={styles.followupQuestion} title={x} onClick={() => onFollowupQuestionClicked(x)}>
                                    {`${x}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}
            <Stack.Item>
                <pre>{debug_data}</pre>
            </Stack.Item>
        </Stack>
    );
};
